import { useMemo } from 'react';
import { useProcedureParams, useUrlPathLocation } from '@medifind/hooks';
import { useRouter } from '@medifind/router';
import { fillTemplate } from '@medifind/utils';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import { HeadingBox } from './components';

/* __ROUTE_PATH__=/procedures/:codingSlug/:projectId/:rest* */
const ProcedureBox = () => {
  const router = useRouter();
  const { codingSlug, projectId } = router.query;

  const { codingName, codingType, codingNotFound } = useRouteStore(selectRouteParams);
  const { urlLocation } = useUrlPathLocation();
  useProcedureParams();

  const links = useMemo(() => {
    return [
      {
        name: 'Overview',
        id: 'overview',
        href: `/procedures/${codingSlug}/${projectId}`,
        paths: [{ matchPath: '/procedures/:codingSlug/:projectId' }],
        // icon: <IconOverview color="white" />
        title: codingName ? `${codingName} Procedure` : null,
        subtitle: 'Description, Latest Information and Doctors',
        subtitleIsH1: true,
        shareBookmark: {
          bookmark: { bookmarkName: codingName, codingSlug, codingType, projectId, type: 'procedure' },
          share: { type: 'page' },
        },
      },
      {
        name: 'Find a Doctor',
        id: 'findADoctor',
        href: `/procedures/${codingSlug}/${projectId}/doctors`,
        paths: [
          { matchPath: '/procedures/:codingSlug/:projectId/doctors/location/:rest*' },
          { matchPath: '/procedures/:codingSlug/:projectId/doctors' },
          { matchPath: '/procedures/:codingSlug/:projectId/doctors/drug/:drugId' },
          { matchPath: '/procedures/:codingSlug/:projectId/doctors/:personSlug/:personId', hideButtons: true },
        ],
        // icon: <IconDoctors color="white" />
        title: codingName
          ? fillTemplate(
              'The 20 Best [codingName:[codingName]] Doctors [!mediumLocationString: Near Me][city: near][mediumLocationString&!city: in][mediumLocationString: [mediumLocationString]]',
              {
                ...urlLocation,
                codingName,
              },
            )
          : null,
        subtitle: codingName ? `Find the Experts and Specialists who perform ${codingName}` : null,
        scrunchCurve: true,
        shareBookmark: {
          bookmark: { bookmarkName: codingName, codingSlug, codingType, projectId, type: 'procedure' },
          share: { type: 'page' },
        },
      },
      {
        name: 'Latest Advances',
        id: 'latestAdvances',
        href: `/procedures/${codingSlug}/${projectId}/treatment-advances`,
        paths: [
          { matchPath: '/procedures/:codingSlug/:projectId/treatment-advances' },
          { matchPath: '/procedures/:codingSlug/:projectId/publications' },
          { matchPath: '/procedures/:codingSlug/:projectId/treatment-advance/:articleId', hideButtons: true },
          { matchPath: '/procedures/:codingSlug/:projectId/publication/:articleId', hideButtons: true },
        ],
        // icon: <IconAdvances color="white" />
        title: codingName ? `${codingName} Latest Advances` : null,
        subtitle: codingName ? `Latest Advances in ${codingName} Procedure` : null,
        shareBookmark: {
          bookmark: { bookmarkName: codingName, codingSlug, codingType, projectId, type: 'procedure' },
          share: { type: 'page' },
        },
      },
      {
        name: 'Clinical Trials',
        id: 'clinicalTrials',
        href: `/procedures/${codingSlug}/${projectId}/clinical-trials`,
        paths: [
          { matchPath: '/procedures/:codingSlug/:projectId/clinical-trials' },
          { matchPath: '/procedures/:codingSlug/:projectId/clinical-trial/:articleId', hideButtons: true },
        ],
        // icon: <IconTrials color="white" />
        title: codingName ? `${codingName} Clinical Trials` : null,
        subtitle: codingName ? `Clinical trials related to ${codingName} Procedure` : null,
        shareBookmark: {
          bookmark: { bookmarkName: codingName, codingSlug, codingType, projectId, type: 'procedure' },
          share: { type: 'page' },
        },
      },
    ];
  }, [codingSlug, projectId, codingName, urlLocation]);

  if (codingNotFound) {
    throw new Error(404);
  }

  return <HeadingBox links={links} />;
};

export { ProcedureBox as default, ProcedureBox };
